import React, { useRef, useEffect, useState, useCallback } from "react";
import Divider from "./Divider";

const AdvSeparator = ({
  children,
  gap = "gap-2",
  dividerWidth = 1,
  minItemsPerRow = 1,
  className = "gap-y-1",
  center = false,
}) => {
  const parentRef = useRef(null);
  const [rows, setRows] = useState([]);
  const itemRefs = useRef([]);

  const getPixelValue = useCallback((tailwindValue) => {
    const base = 4;
    const value = parseInt(tailwindValue.replace(/[^\d]/g, ""));
    return value * base;
  }, []);

  const calculateRows = useCallback(() => {
    if (!parentRef.current) return;

    const parentWidth = parentRef.current.offsetWidth;
    const gapSize = getPixelValue(gap);
    const actualDividerWidth = dividerWidth * 16; // Convert rem to pixels
    const childrenArray = React.Children.toArray(children);

    if (childrenArray.length === 0 || parentWidth <= 0) {
      setRows([]);
      return;
    }

    // Wait for all items to be rendered before measuring
    setTimeout(() => {
      const measurements = itemRefs.current.map((ref, index) => {
        if (!ref) return { width: 100, height: 30 }; // Fallback

        const rect = ref.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(ref);

        return {
          width: Math.ceil(
            rect.width +
              parseFloat(computedStyle.marginLeft || 0) +
              parseFloat(computedStyle.marginRight || 0)
          ),
          height: Math.ceil(
            rect.height +
              parseFloat(computedStyle.marginTop || 0) +
              parseFloat(computedStyle.marginBottom || 0)
          ),
        };
      });

      let currentRow = [];
      let currentRowWidth = 0;
      let maxRowHeight = 0;
      const newRows = [];

      for (let index = 0; index < childrenArray.length; index++) {
        const { width, height } = measurements[index];
        maxRowHeight = Math.max(maxRowHeight, height);

        // Calculate spacing needed for this item
        const needsDivider = currentRow.length > 0;
        const itemSpacing = needsDivider ? actualDividerWidth + gapSize : 0;
        const totalItemWidth = width + itemSpacing;

        // Check if adding this item would exceed the width
        const wouldExceedWidth = currentRowWidth + totalItemWidth > parentWidth;
        const hasMinItems = currentRow.length >= minItemsPerRow;

        // Decision logic for wrapping
        if (wouldExceedWidth && hasMinItems && currentRow.length > 0) {
          // Wrap to new row
          newRows.push({ indices: [...currentRow], height: maxRowHeight });
          currentRow = [index];
          currentRowWidth = width;
          maxRowHeight = height;
        } else {
          // Add to current row
          currentRow.push(index);
          currentRowWidth += totalItemWidth;
        }

        // Handle last item
        if (index === childrenArray.length - 1 && currentRow.length > 0) {
          newRows.push({ indices: [...currentRow], height: maxRowHeight });
        }
      }

      setRows(newRows);
    }, 10); // Small delay to ensure rendering is complete
  }, [children, gap, dividerWidth, minItemsPerRow, getPixelValue]);

  useEffect(() => {
    // Initialize refs array
    itemRefs.current = itemRefs.current.slice(
      0,
      React.Children.count(children)
    );

    let timeoutId;
    const debouncedCalculate = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(calculateRows, 50);
    };

    const resizeObserver = new ResizeObserver(debouncedCalculate);

    if (parentRef.current) {
      resizeObserver.observe(parentRef.current);
      calculateRows();
    }

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [calculateRows]);

  // Recalculate when children change
  useEffect(() => {
    calculateRows();
  }, [children]);

  const childrenArray = React.Children.toArray(children);

  // If no rows calculated yet, render all items in hidden state for measurement
  if (rows.length === 0) {
    return (
      <div ref={parentRef} className={`w-full flex flex-col ${className}`}>
        <div className="flex flex-wrap opacity-0 pointer-events-none absolute">
          {childrenArray.map((child, index) => (
            <div
              key={index}
              ref={(el) => (itemRefs.current[index] = el)}
              className="inline-block"
            >
              {child}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div ref={parentRef} className={`w-full flex flex-col ${className}`}>
      {/* Hidden measurement elements */}
      <div className="flex flex-wrap opacity-0 pointer-events-none absolute">
        {childrenArray.map((child, index) => (
          <div
            key={index}
            ref={(el) => (itemRefs.current[index] = el)}
            className="inline-block"
          >
            {child}
          </div>
        ))}
      </div>

      {/* Visible rows */}
      {rows.map(({ indices, height }, rowIndex) => (
        <React.Fragment key={rowIndex}>
          <div
            className={`flex items-center ${gap} ${
              center ? "justify-center" : "justify-start"
            }`}
            style={{ minHeight: `${height}px` }}
          >
            {indices.map((childIndex, index) => (
              <React.Fragment key={childIndex}>
                {index > 0 && (
                  <Divider
                    orientation="vertical"
                    style={{ width: `${dividerWidth}rem` }}
                  />
                )}
                {childrenArray[childIndex]}
              </React.Fragment>
            ))}
          </div>
          {rowIndex < rows.length - 1 && <Divider orientation="horizontal" />}
        </React.Fragment>
      ))}
    </div>
  );
};

export default AdvSeparator;
